import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useProjects, useStreak, useContentProgress, useDiagnostic } from '@/hooks/useParticipantData';
import { 
  Flame, FolderOpen, GraduationCap, BookOpen
} from 'lucide-react';

const levelInfo: Record<string, { number: number; color: string }> = {
  'Iniciador': { number: 1, color: 'hsl(var(--chart-1))' },
  'Explorador': { number: 2, color: 'hsl(var(--chart-2))' },
  'Experimentador': { number: 3, color: 'hsl(var(--chart-3))' },
  'Implementador': { number: 4, color: 'hsl(var(--chart-4))' },
  'Integrador': { number: 5, color: 'hsl(var(--chart-5))' },
  'Transformador': { number: 6, color: 'hsl(var(--primary))' },
};

export default function MetricsSection() {
  const { data: projects = [] } = useProjects();
  const { data: streak } = useStreak();
  const { data: contentProgress } = useContentProgress();
  const { data: diagnostic } = useDiagnostic();

  const totalProjects = projects.length;
  const completedProjects = projects.filter(p => p.status === 'completed').length;
  const currentStreak = streak?.current_streak ?? 0;
  const contentPercent = contentProgress?.percent ?? 0;

  const diagLevel = diagnostic?.dimension_scores 
    ? Object.keys(levelInfo).find(k => {
        const scores = diagnostic.dimension_scores as any;
        return scores?.level === k;
      })
    : null;
  const diagMaturity = diagnostic?.maturity_level ?? null;
  const diagPercentage = diagMaturity ? Math.min(100, Math.round((diagMaturity / 5) * 100)) : 0;

  return (
    <div className="space-y-6">
      {/* Main Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="glass-card border-primary/20">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <FolderOpen className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-3xl font-bold text-foreground">{completedProjects}/{totalProjects}</p>
                <p className="text-sm text-muted-foreground">Proyectos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-success/20">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-success" />
              </div>
              <div>
                <p className="text-3xl font-bold text-foreground">{contentPercent}%</p>
                <p className="text-sm text-muted-foreground">Contenido</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-warning/20">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-warning/10 flex items-center justify-center">
                <Flame className="w-6 h-6 text-warning" />
              </div>
              <div>
                <p className="text-3xl font-bold text-foreground">{currentStreak}</p>
                <p className="text-sm text-muted-foreground">Días en Racha</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Diagnostic Level */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" />
            Nivel de Madurez IA
          </CardTitle>
          <CardDescription>Tu clasificación en el diagnóstico</CardDescription>
        </CardHeader>
        <CardContent>
          {diagnostic ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center text-white font-bold text-2xl"
                  style={{ backgroundColor: levelInfo[diagLevel ?? '']?.color || 'hsl(var(--primary))' }}
                >
                  {diagnostic.maturity_level ?? '?'}
                </div>
                <div>
                  <p className="text-xl font-bold text-foreground">Nivel {diagnostic.maturity_level ?? '?'}</p>
                  <p className="text-sm text-muted-foreground">
                    Diagnóstico completado
                  </p>
                </div>
              </div>
              <Progress value={diagPercentage} className="h-3" />
              <p className="text-sm text-muted-foreground text-center">
                {diagPercentage}% de madurez en IA
              </p>
            </div>
          ) : (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">No has completado el diagnóstico</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}